import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types';
import './UserManagement.css';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'admin' | 'agent'>('all');
  const { userProfile } = useAuth();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersSnapshot = await getDocs(collection(db, 'userProfiles'));
      const usersData = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt
        };
      }) as User[];
      
      // Sort by creation date (newest first)
      usersData.sort((a, b) => {
        if (!a.createdAt || !b.createdAt) return 0;
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });
      
      setUsers(usersData);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(`Failed to load users: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId: string, isAdmin: boolean) => {
    try {
      setSaving(userId);
      setError(null);
      
      const updateData: any = {};
      if (isAdmin) {
        updateData.role = 'admin';
      } else {
        updateData.role = null;
      }
      
      await updateDoc(doc(db, 'userProfiles', userId), updateData);

      setUsers(users.map(user => 
        user.uid === userId ? { ...user, role: isAdmin ? 'admin' : undefined } : user
      ));

      if (userId === userProfile?.uid) {
        window.location.reload();
      }
    } catch (err: any) {
      console.error('Error updating user role:', err);
      setError(`Failed to update user role: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(null);
    }
  };

  // Filter users based on search and type filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.nmls?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = 
      filterType === 'all' ? true :
      filterType === 'admin' ? user.role === 'admin' :
      !user.role;

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p className="page-subtitle">
            Manage user roles and permissions. Total users: <strong>{users.length}</strong>
          </p>
        </div>
        <button onClick={fetchUsers} className="refresh-btn" disabled={loading}>
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div className="error-message">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search by name, email, or NMLS..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-buttons">
          <button
            className={filterType === 'all' ? 'active' : ''}
            onClick={() => setFilterType('all')}
          >
            All Users ({users.length})
          </button>
          <button
            className={filterType === 'admin' ? 'active' : ''}
            onClick={() => setFilterType('admin')}
          >
            Admins ({users.filter(u => u.role === 'admin').length})
          </button>
          <button
            className={filterType === 'agent' ? 'active' : ''}
            onClick={() => setFilterType('agent')}
          >
            Agents ({users.filter(u => !u.role).length})
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>User Type</th>
              <th>NMLS</th>
              <th>Role</th>
              <th>Verification</th>
              <th>Joined</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-results">
                  No users found matching your criteria
                </td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.uid}>
                  <td className="user-name">
                    <div className="user-avatar">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </div>
                    <span>{user.firstName} {user.lastName}</span>
                  </td>
                  <td className="user-email">{user.email}</td>
                  <td>
                    <span className="badge badge-type">
                      {user.userType === 'loan_originator' ? '🏦 Lender' : '🏠 Agent'}
                    </span>
                  </td>
                  <td className="user-nmls">{user.nmls || '—'}</td>
                  <td>
                    <select
                      value={user.role === 'admin' ? 'admin' : 'agent'}
                      onChange={(e) => updateUserRole(user.uid, e.target.value === 'admin')}
                      disabled={saving === user.uid}
                      className="role-select"
                    >
                      <option value="agent">Agent</option>
                      <option value="admin">Admin</option>
                    </select>
                    {saving === user.uid && <span className="saving-indicator">💾</span>}
                  </td>
                  <td>
                    <span className={`badge badge-${user.verificationStatus || 'pending'}`}>
                      {user.verificationStatus || 'pending'}
                    </span>
                  </td>
                  <td className="user-date">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    }) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Stats Footer */}
      <div className="stats-footer">
        <div className="stat-item">
          <span className="stat-label">Showing:</span>
          <span className="stat-value">{filteredUsers.length} of {users.length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Admins:</span>
          <span className="stat-value">{users.filter(u => u.role === 'admin').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Agents:</span>
          <span className="stat-value">{users.filter(u => !u.role).length}</span>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
